import React, { useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { CalendarClock, CheckCircle2, UserRound } from "lucide-react";
import Spinner1 from "../common/spinners";
import { formattedDate, formattedDate2, formattedDate3 } from "../../utils/utilities";

const statusClassMap = {
  Missed: "is-danger",
  Approved: "is-success",
  Completed: "is-success",
  Cancelled: "is-neutral",
  "Pending Paypoint Approval": "is-warning",
};

const AppointmentHistoryTab = ({ data, loading }) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const filteredData = useMemo(
    () =>
      (Array.isArray(data) ? data : []).filter(
        (item) =>
          item?.whomToSee?.first_name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.whomToSee?.last_name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.department?.name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.whomToSee?.profession?.toLowerCase()?.includes(searchTerm.toLowerCase())
      ),
    [data, searchTerm]
  );

  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const openDetailsModal = (item) => {
    setSelectedAppointment(item);
    setShowDetailsModal(true);
  };

  return (
    <div className="w-100">
      <div className="data-panel-head">
        <div className="data-panel-copy">
          <h5>Appointment history</h5>
          <p>Review previous visits, statuses, and appointment details in one cleaner timeline.</p>
        </div>
        <div className="data-panel-actions">
          <div className="data-search">
            <Form.Control
              type="text"
              placeholder="Search doctor, specialty, or department"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="data-table-shell">
        {loading ? (
          <div className="w-100 d-flex justify-content-center py-5">
            <Spinner1 />
          </div>
        ) : currentItems.length === 0 ? (
          <div className="data-table-empty">
            <h6>No appointment history found</h6>
            <p>Completed, cancelled, and older visits will appear here when available.</p>
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="table data-table align-middle w-100">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Consultant</th>
                  <th>Department</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item) => (
                  <tr key={item.id} onClick={() => openDetailsModal(item)} style={{ cursor: "pointer" }}>
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <span className="data-icon-avatar">
                          <CalendarClock size={18} />
                        </span>
                        <div>
                          <div className="data-cell-title">{formattedDate2(item?.createdAt)}</div>
                          <div className="data-cell-meta">{formattedDate3(item?.createdAt)}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <span className="data-icon-avatar">
                          <UserRound size={18} />
                        </span>
                        <div>
                          <div className="data-cell-title text-capitalize">
                            {item?.whomToSee?.first_name} {item?.whomToSee?.last_name}
                          </div>
                          <div className="data-cell-meta text-capitalize">
                            {item?.whomToSee?.profession}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell-title">{item?.department?.name}</div>
                      <div className="data-cell-meta">{item?.department?.slug || "Visit department"}</div>
                    </td>
                    <td>
                      <span className={`data-status-badge ${statusClassMap[item?.status] || "is-neutral"}`}>
                        {item?.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredData.length > 0 && (
          <div className="data-pagination">
            <Button
              variant="light"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((page) => page - 1)}
            >
              Previous
            </Button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="light"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((page) => page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Appointment Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAppointment && (
            <div className="d-grid gap-3">
              <p className="mb-0">
                <strong>Consultant:</strong>{" "}
                <span className="text-success text-capitalize">
                  {selectedAppointment?.whomToSee?.first_name} {selectedAppointment?.whomToSee?.last_name}
                </span>
              </p>
              <p className="mb-0">
                <strong>Consultation Type:</strong> {selectedAppointment?.consultation_type || "---"}
              </p>
              <p className="mb-0">
                <strong>Consulting Room:</strong> {selectedAppointment?.consultingRoom?.name || "---"}
              </p>
              <p className="mb-0">
                <strong>Department:</strong> {selectedAppointment?.department?.name || "---"}
              </p>
              <p className="mb-0">
                <strong>Date:</strong> <span className="text-danger">{formattedDate(selectedAppointment?.createdAt)}</span>
              </p>
              <p className="mb-0">
                <strong>Appointment Type:</strong> {selectedAppointment?.appointmentType || "---"}
              </p>
              <p className="mb-0">
                <strong>Reason:</strong> {selectedAppointment?.description || "---"}
              </p>
              <div className="d-flex align-items-center gap-2 text-success">
                <CheckCircle2 size={16} />
                <span>Selected record loaded successfully.</span>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AppointmentHistoryTab;
